import useNestedTracksStore, {
	NestedTracksTab,
} from '@/stores/nestedTracksStore'

import {
	NESTED_TRACKS_TRAY_ID,
	NESTED_TRACKS_TRAY_SCROLL_CONTAINER_SELECTOR,
} from './NestedTracks.constants'

const getNestedTracksTrayDialog = (): HTMLDialogElement | null => {
	if (typeof document === 'undefined') {
		return null
	}

	const dialog = document.getElementById(NESTED_TRACKS_TRAY_ID)

	return dialog instanceof HTMLDialogElement ? dialog : null
}

const supportsNativeDialogApi = (): boolean =>
	typeof HTMLDialogElement !== 'undefined' &&
	typeof HTMLDialogElement.prototype.showModal === 'function' &&
	typeof HTMLDialogElement.prototype.close === 'function'

export const isNestedTracksTrayOpen = (): boolean => {
	const dialog = getNestedTracksTrayDialog()

	if (!dialog) {
		return false
	}

	return dialog.open || dialog.hasAttribute('open')
}

export const openNestedTracksTray = (): void => {
	const dialog = getNestedTracksTrayDialog()

	if (!dialog || isNestedTracksTrayOpen()) {
		return
	}

	if (supportsNativeDialogApi()) {
		try {
			dialog.showModal()
		} catch {
			dialog.setAttribute('open', '')
		}
		return
	}

	dialog.setAttribute('open', '')
}

/** Scroll container is marked in NestedTracks once the library Tray mounts. */
export const scrollNestedTracksTrayToTop = (): void => {
	if (typeof document === 'undefined') {
		return
	}

	const dialog = getNestedTracksTrayDialog()
	const scrollContainer = dialog?.querySelector(
		NESTED_TRACKS_TRAY_SCROLL_CONTAINER_SELECTOR
	)

	if (!(scrollContainer instanceof HTMLElement)) {
		return
	}

	scrollContainer.scrollTop = 0
}

export const closeNestedTracksTray = (): void => {
	if (typeof document === 'undefined') {
		return
	}

	const dialog = getNestedTracksTrayDialog()

	if (!dialog || !isNestedTracksTrayOpen()) {
		return
	}

	if (supportsNativeDialogApi()) {
		try {
			dialog.close()
		} catch {
			dialog.removeAttribute('open')
		}
		return
	}

	dialog.removeAttribute('open')
}

/**
 * Shows a track in the tray on the given tab. Opening from outside the tray
 * starts a fresh history; opening from inside it pushes onto the history.
 *
 * Callers are also `command="show-modal"` invokers. We still open from script
 * because, without native invoker support, the library Tray only wires the
 * invokers present when it mounted — later rows and rows inside the tray would
 * never open it. Natively, this runs first and the command finds it open.
 */
export const showTrackInNestedTracksTray = (
	track: Track,
	source: string,
	tab: NestedTracksTab
): void => {
	const trayWasClosed = !isNestedTracksTrayOpen()
	const { navigateForTrack, track: currentTrack } =
		useNestedTracksStore.getState()

	navigateForTrack(track, source, { resetStack: currentTrack === null, tab })

	if (trayWasClosed) {
		queueMicrotask(openNestedTracksTray)
	}
}
